import { useState } from 'react'

import { feltTemperature } from '../../lib/physics'
import { Choice, Predict, useFrames } from './common'

/**
 * Три миски (уроки 2–3): одна рука в горячей воде, другая в холодной, потом обе — в тёплую.
 * Одной руке тёплая вода кажется холодной, другой — тёплой; термометр показывает одно число.
 * Ощущения обманывают так же, как глаза, — поэтому физика измеряет.
 */

const МИСКИ = [
  { имя: 'горячая', где: 'в горячей', t: 45, x: 70 },
  { имя: 'тёплая', где: 'в тёплой', t: 25, x: 180 },
  { имя: 'холодная', где: 'в холодной', t: 8, x: 290 },
] as const

export default function BowlsLab() {
  const [левая, setЛевая] = useState(0) // откуда левая рука: 0 — горячая, 2 — холодная
  const [перенесли, setПеренесли] = useState(false)
  const [ход, setХод] = useState(0) // 0…1 — руки едут в среднюю миску

  useFrames((dt) => setХод((h) => Math.max(0, Math.min(1, h + (перенесли ? dt : -dt) * 1.5))))

  const правая = 2 - левая
  const вода = МИСКИ[1].t
  const чувствЛ = feltTemperature(МИСКИ[левая].t, вода)
  const чувствП = feltTemperature(МИСКИ[правая].t, вода)
  const на = (откуда: number, сдвиг: number) => МИСКИ[откуда].x + (МИСКИ[1].x + сдвиг - МИСКИ[откуда].x) * ход
  const цвет = (t: number) => (t > 30 ? 'fill-hot' : t < 15 ? 'fill-cold' : 'fill-warm')

  const рука = (x: number, подпись: string) => (
    <g transform={`translate(${x} ${70 + 20 * Math.sin(ход * Math.PI)})`}>
      <rect x={-9} y={-50} width={18} height={70} rx={8} className="fill-skin ink" strokeWidth={1} />
      <text x={0} y={-56} textAnchor="middle" className="lab-tick">{подпись}</text>
    </g>
  )

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 200" role="img" aria-label="Три миски с водой">
        {МИСКИ.map((м) => (
          <g key={м.имя}>
            <path d={`M${м.x - 46} 100 Q${м.x} 170 ${м.x + 46} 100 Z`} className={`${цвет(м.t)} ink`} strokeWidth={1.5} />
            <text x={м.x} y={190} textAnchor="middle" className="lab-label">{м.имя}</text>
            <text x={м.x} y={126} textAnchor="middle" className="lab-tick">{перенесли || м.t === 25 ? `${м.t} °C` : '?'}</text>
          </g>
        ))}
        {рука(на(левая, -16), 'левая')}
        {рука(на(правая, 16), 'правая')}
      </svg>

      {перенесли && ход > 0.95 ? (
        <div className="lab-readout lab-readout-col">
          <span>левая рука (была {МИСКИ[левая].где}): «{чувствЛ}!»</span>
          <span>правая рука (была {МИСКИ[правая].где}): «{чувствП}!»</span>
          <span className="el">термометр в средней миске: {вода} °C — одно число для обеих рук</span>
        </div>
      ) : (
        <div className="lab-caption">Руки привыкают к своей воде. Теперь перенеси обе в среднюю миску.</div>
      )}

      <Predict
        question="Обе руки опустили в одну и ту же тёплую воду. Что они почувствуют?"
        options={['обе — одинаково тепло', 'одной холодно, другой тепло', 'обеим холодно']}
        right={1}
        revealed={перенесли && ход > 0.95}
        resetKey={String(левая)}
        explain="Рука чувствует не температуру воды, а разницу с тем, к чему привыкла. Поэтому «жарко» и «холодно» — не физические величины, а 25 °C на термометре — величина."
      />

      <div className="lab-sub">Откуда левая рука</div>
      <Choice
        options={[
          { value: 0, label: 'из горячей' },
          { value: 2, label: 'из холодной' },
        ]}
        value={левая}
        onChange={(v) => { setЛевая(v); setПеренесли(false) }}
      />
      <div className="row-wrap" style={{ marginTop: 12 }}>
        {перенесли ? (
          <button className="btn" onClick={() => setПеренесли(false)}>↩️ Вернуть руки</button>
        ) : (
          <button className="btn btn-primary" onClick={() => setПеренесли(true)}>🤲 Обе руки — в тёплую</button>
        )}
      </div>
      <p className="muted small">
        Опыт можно повторить дома с тремя мисками. Не верь ощущениям — измеряй: так начинается
        физическая величина.
      </p>
    </div>
  )
}
